import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { JIRA_TEAM_SELECT_LABELS } from './jiraIntegration.constants';

function NRTeamOptionLabel({ avatarUrl, name }) {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Avatar src={avatarUrl || undefined} alt={name} sx={{ width: 20, height: 20, fontSize: 10 }}>
        {name?.charAt(0)?.toUpperCase()}
      </Avatar>
      <Typography component="span" variant="body2">
        {name}
      </Typography>
    </Box>
  );
}

/**
 * Searchable Team select for Jira create — avatar + name like assignee,
 * Autocomplete search to match Jira's Team picker.
 */
function NRJiraTeamSelect({ id, label, value, onChange, teams = [], disabled = false, required = false, error = false, helperText, sxProps }) {
  const theme = useTheme();
  const options = useMemo(
    () =>
      (teams || [])
        .map(team => ({
          value: team.id,
          label: team.displayName || team.id,
          avatarUrl: team.avatarUrl
        }))
        .filter(option => option.value),
    [teams]
  );

  const selectedOption = useMemo(() => options.find(option => option.value === value) || null, [options, value]);

  return (
    <Autocomplete
      id={id}
      options={options}
      value={selectedOption}
      disabled={disabled}
      onChange={(_, nextOption) => onChange(nextOption?.value || '')}
      getOptionLabel={option => option.label || ''}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={option.value ?? key} {...optionProps}>
            <NRTeamOptionLabel avatarUrl={option.avatarUrl} name={option.label} />
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={selectedOption ? undefined : JIRA_TEAM_SELECT_LABELS.SELECT}
          color="secondary"
          sx={sxProps}
        />
      )}
      componentsProps={{
        popper: {
          sx: {
            boxShadow: theme.shadows[3],
            borderRadius: 1.5
          }
        }
      }}
      filterOptions={(optionList, state) => {
        const query = state.inputValue.trim().toLowerCase();
        if (!query) {
          return optionList;
        }
        return optionList.filter(option => option.label.toLowerCase().includes(query));
      }}
      noOptionsText={JIRA_TEAM_SELECT_LABELS.SEARCH}
      clearOnBlur
      handleHomeEndKeys
      fullWidth
    />
  );
}

export default NRJiraTeamSelect;
